/**
 * ProjectSettingsModal — nastavení Codable projektu.
 * Jedna plocha, žádné záložky. Přehled + ovládání přístupu + zdroje.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Button, Modal } from '../../../components/ui';
import {
  Activity,
  BookOpen,
  ChevronRight,
  Copy,
  Cpu,
  Database,
  ExternalLink,
  FileText,
  GitBranch,
  History,
  Layers,
  ListChecks,
  RefreshCw,
} from 'lucide-react';
import ProjectAccessPanel from './ProjectAccessPanel';
import ProjectConnectionsPanel from './ProjectConnectionsPanel';
import { ProjectMembersPanel } from './ProjectMembersPanel';
import { ProjectBackupCard } from '../../../components/ProjectBackupCard';

interface ProjectSettingsModalProps {
  name: string;
  slug: string;
  description?: string | null;
  previewUrl: string;
  prodUrl?: string | null;
  mcpUrl?: string | null;
  mcpEnabled?: boolean;
  ssoMode?: 'off' | 'platform' | 'custom';
  appKeyEnabled?: boolean;
  oauthEnabled?: boolean;
  oauthPlatformEnabled?: boolean;
  topologyLabel?: string | null;
  gitlabUrl: string | null;
  gitlabPath?: string | null;
  projectId?: string | null;
  backend: { type: string; enabled: boolean } | null;
  source?: 'native' | 'lovable' | 'acm';
  origin?: {
    node?: string;
    endpoint?: string;
    mode?: 'clone' | 'mirror';
    stale?: boolean;
  } | null;
  envName?: string | null;
  orchestratorProjectId?: string | null;
  workspaceService?: string | null;
  productionService?: string | null;
  workspaceInstance?: string | null;
  prodInstance?: string | null;
  services?: Record<string, unknown> | null;
  previewOn?: boolean;
  previewActual?: boolean;
  publishOn?: boolean;
  publishActual?: boolean;
  publishSyncing?: boolean;
  slotPreviewUrl?: string | null;
  enabling: boolean;
  onEnableBackend: () => void;
  onOpenKnowledge: (tab?: 'instructions' | 'sources') => void;
  onOpenActivity: () => void;
  onOpenHistory: () => void;
  onOpenProjectLog: () => void;
  onOpenIde: () => void;
  onOpenLlmModels: () => void;
  onOpenTopology?: () => void;
  onDeployed?: () => void;
  onClose: () => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className='space-y-2'>
      <h3 className='text-sm font-semibold text-white'>{title}</h3>
      {children}
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex items-start justify-between gap-3 px-3 py-2.5 text-sm'>
      <span className='text-gray-500 shrink-0'>{label}</span>
      <div className='min-w-0 text-right text-gray-200'>{children}</div>
    </div>
  );
}

function LinkRow(
  { icon: Icon, label, hint, onClick }: {
    icon: typeof BookOpen;
    label: string;
    hint?: string;
    onClick: () => void;
  },
) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 hover:border-gray-700 hover:bg-gray-800/40'
    >
      <Icon size={15} className='text-gray-400 shrink-0' />
      <div className='min-w-0 flex-1'>
        <div className='text-sm text-white'>{label}</div>
        {hint && <div className='text-[11px] text-gray-500 truncate'>{hint}</div>}
      </div>
      <ChevronRight size={14} className='text-gray-600 shrink-0' />
    </button>
  );
}

export default function ProjectSettingsModal(props: ProjectSettingsModalProps) {
  const { t } = useTranslation();
  const {
    name,
    slug,
    description = null,
    previewUrl,
    prodUrl = null,
    mcpUrl = null,
    mcpEnabled = false,
    ssoMode = 'off',
    appKeyEnabled = false,
    oauthEnabled = false,
    oauthPlatformEnabled = false,
    topologyLabel = null,
    gitlabUrl,
    gitlabPath = null,
    projectId = null,
    backend,
    source = 'native',
    origin,
    envName,
    orchestratorProjectId,
    workspaceService = null,
    productionService = null,
    workspaceInstance = null,
    prodInstance = null,
    services = null,
    previewOn = false,
    previewActual,
    publishOn = false,
    publishActual,
    publishSyncing = false,
    slotPreviewUrl = null,
    enabling,
    onEnableBackend,
    onOpenKnowledge,
    onOpenActivity,
    onOpenHistory,
    onOpenProjectLog,
    onOpenIde,
    onOpenLlmModels,
    onOpenTopology,
    onDeployed,
    onClose,
  } = props;
  const canOrch = Boolean(orchestratorProjectId);

  const callOrch = async (tool: string, args: Record<string, unknown> = {}) => {
    if (!orchestratorProjectId) throw new Error(t('codable.settingsNeedOrch'));
    const r = await api.mcp<{ ok?: boolean; error?: string }>(tool, args);
    if (r.ok === false) throw new Error(r.error ?? t('codable.errorShort', 'chyba'));
    return r;
  };

  const [deploying, setDeploying] = useState(false);
  const deployFromGit = async () => {
    if (deploying) return;
    setDeploying(true);
    try {
      await callOrch(`${orchestratorProjectId}.orchestrator.sync`);
      toast.success(t('codable.settingsGitSyncDone'));
      onDeployed?.();
    } catch (e) {
      toast.error(String((e as Error).message));
    } finally {
      setDeploying(false);
    }
  };

  const [resyncing, setResyncing] = useState(false);
  const resync = async () => {
    if (resyncing) return;
    setResyncing(true);
    try {
      const r = await api.mcp<{ ok?: boolean; error?: string; stale?: boolean; mode?: string }>(
        `${orchestratorProjectId}.orchestrator.resync`,
        {},
      );
      if (r.ok === false) throw new Error(r.error ?? 'resync');
      if (r.stale) toast.error(t('codable.settingsSourceOffline'));
      else {
        toast.success(t('codable.resyncTitle') + (r.mode ? ` (${r.mode})` : ''));
        onDeployed?.();
      }
    } catch (e) {
      toast.error(String((e as Error).message));
    } finally {
      setResyncing(false);
    }
  };

  const knowledgeQ = useQuery({
    queryKey: ['codable-knowledge', slug],
    queryFn: () =>
      api.mcp<{ ok: boolean; knowledge?: string | null }>('codable.knowledge', {
        slug,
        action: 'get',
      }),
  });
  const treeQ = useQuery({
    queryKey: ['codable-tree', slug],
    queryFn: () =>
      api.mcp<{ ok: boolean; entries?: Array<{ path: string; type: string }> }>(
        'codable.files',
        { slug, action: 'tree' },
      ),
    staleTime: 60_000,
  });
  const docs = (treeQ.data?.entries ?? []).filter(
    (e) => e.type === 'blob' && (e.path === 'README.md' || /^docs\/.+\.md$/.test(e.path)),
  );
  const hasReadme = docs.some((d) => d.path === 'README.md');
  const hasMemory = docs.some((d) => d.path === 'docs/agent/MEMORY.md');
  const instructionLen = (knowledgeQ.data?.knowledge ?? '').trim().length;
  const cloneUrl = gitlabUrl ? `${gitlabUrl.replace(/\/$/, '')}.git` : null;
  const copyText = (value: string, ok: string) => {
    navigator.clipboard.writeText(value).then(
      () => toast.success(ok),
      () => toast.error(t('codable.settingsCopyFail')),
    );
  };
  const sourceLabel = source === 'lovable'
    ? t('codable.originSourceLovable', 'Lovable (MCP)')
    : source === 'acm'
    ? t('codable.originSourceAcm', 'Jiný ACM nod')
    : t('codable.originSourceNative', 'Native (CodAble)');
  return (
    <Modal
      open
      onClose={onClose}
      title={t('codable.projectSettingsTitle', 'Nastavení projektu — {{name}}', { name })}
      size='lg'
    >
      <div className='space-y-6'>
        <Section title={t('codable.settingsTabOverview', 'Přehled')}>
          <div className='rounded-xl border border-gray-800 bg-gray-900/40 divide-y divide-gray-800/60'>
            <div className='px-4 py-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <div className='text-base font-semibold text-white truncate'>{name}</div>
                  <div className='mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 font-mono'>
                    <span>{slug}</span>
                    {projectId && (
                      <button
                        type='button'
                        className='hover:text-gray-200'
                        onClick={() => copyText(projectId, t('ui.copied', 'Zkopírováno'))}
                      >
                        {projectId}
                      </button>
                    )}
                  </div>
                </div>
                {onOpenTopology
                  ? (
                    <button
                      type='button'
                      className='shrink-0 text-xs text-plex hover:underline pt-1'
                      onClick={onOpenTopology}
                    >
                      {topologyLabel ?? t('codable.settingsTopology', 'Typ')}
                      {' · '}
                      {t('codable.settingsTopologyChange', 'Změnit')}
                    </button>
                  )
                  : (
                    <span className='shrink-0 text-xs text-gray-400 pt-1'>
                      {topologyLabel ?? '—'}
                    </span>
                  )}
              </div>
              {description?.trim() && (
                <p className='mt-2 text-sm text-gray-300 whitespace-pre-wrap'>{description.trim()}</p>
              )}
            </div>
            <div className='px-4 py-3 flex items-center gap-3'>
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  backend?.enabled
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-gray-500/10 text-gray-400'
                }`}
              >
                <Database size={16} />
              </div>
              <div className='min-w-0 flex-1'>
                <div className='text-sm text-white'>
                  {backend?.enabled
                    ? t('codable.settingsSupabaseOn')
                    : t('codable.settingsFrontendOnly')}
                </div>
                <div className='text-[11px] text-gray-500'>
                  {backend?.enabled
                    ? backend.type
                    : t('codable.settingsFrontendOnlyHint')}
                </div>
              </div>
              {backend && !backend.enabled && (
                <Button
                  size='sm'
                  variant='primary'
                  icon={<Database size={14} />}
                  loading={enabling}
                  onClick={onEnableBackend}
                >
                  {t('codable.backend')}
                </Button>
              )}
            </div>
          </div>
        </Section>

        <Section title={t('codable.settingsTabAccess', 'Přístup')}>
          <ProjectAccessPanel
            orchestratorProjectId={orchestratorProjectId}
            previewUrl={previewUrl}
            prodUrl={prodUrl}
            mcpUrl={mcpUrl}
            mcpEnabled={mcpEnabled}
            slug={slug}
            ssoMode={ssoMode}
            appKeyEnabled={appKeyEnabled}
            oauthEnabled={oauthEnabled}
            oauthPlatformEnabled={oauthPlatformEnabled}
            workspaceService={workspaceService}
            productionService={productionService}
            workspaceInstance={workspaceInstance}
            prodInstance={prodInstance}
            services={services}
            previewOn={previewOn}
            previewActual={previewActual}
            publishOn={publishOn}
            publishActual={publishActual}
            publishSyncing={publishSyncing}
            slotPreviewUrl={slotPreviewUrl}
            onOpenIde={onOpenIde}
          />
        </Section>

        <Section title={t('hubConn.sectionTitle', 'Propojení')}>
          <ProjectConnectionsPanel slug={slug} />
        </Section>

        <Section title={t('codableMembers.projectAccess', 'Vlastník, členové a oprávnění')}>
          <ProjectMembersPanel project={{ id: projectId || slug, slug, name }} />
        </Section>

        <Section title={t('codable.settingsTabSources', 'Zdroje')}>
          <div className='space-y-3'>
            <div className='rounded-xl border border-gray-800 bg-gray-900/40 divide-y divide-gray-800/60 text-sm'>
              <MetaRow label={t('codable.sourceLabel', 'Původ')}>{sourceLabel}</MetaRow>
              {source === 'native' && (
                <div className='px-3 py-2 text-[11px] text-gray-500'>
                  {t(
                    'codable.settingsSourceNativeHint',
                    'Projekt vznikl v CodAble. Kód žije v GitLabu tohoto ACM.',
                  )}
                </div>
              )}
              {origin?.node && (
                <MetaRow label={t('codable.acmNode', 'ACM nod')}>
                  <code className='font-mono text-xs'>{origin.node}</code>
                </MetaRow>
              )}
              {origin?.endpoint && (
                <MetaRow label={t('codable.settingsOriginEndpoint', 'Endpoint')}>
                  <code className='font-mono text-xs break-all'>{origin.endpoint}</code>
                </MetaRow>
              )}
              {origin?.mode && (
                <MetaRow label={t('codable.mode', 'Režim')}>
                  {origin.mode === 'mirror'
                    ? t('codable.modeMirror', 'Živý mirror (edituje se na dálku)')
                    : t('codable.modeClone', 'Klon (kód žije u nás)')}
                </MetaRow>
              )}
              {source !== 'native' && canOrch && (
                <div className='flex items-center justify-between gap-3 px-3 py-2.5'>
                  <div className='text-[11px] text-gray-500'>
                    {origin?.stale
                      ? t('codable.resyncStale', 'Zdroj byl offline — zkus znovu.')
                      : t('codable.resyncHint')}
                  </div>
                  <Button
                    size='sm'
                    variant='secondary'
                    icon={<RefreshCw size={14} />}
                    loading={resyncing}
                    onClick={resync}
                  >
                    {t('codable.resync')}
                  </Button>
                </div>
              )}
            </div>

            <div className='rounded-xl border border-gray-800 bg-gray-900/40 divide-y divide-gray-800/60'>
              {gitlabUrl
                ? (
                  <>
                    <div className='flex items-center gap-2 px-3 py-2.5'>
                      <GitBranch size={13} className='text-gray-500 shrink-0' />
                      <a
                        href={gitlabUrl}
                        target='_blank'
                        rel='noreferrer'
                        className='flex-1 min-w-0 text-xs font-mono text-plex truncate hover:underline'
                      >
                        {gitlabPath || gitlabUrl.replace(/^https?:\/\//, '')}
                      </a>
                      <button
                        type='button'
                        className='text-gray-500 hover:text-gray-200'
                        onClick={() => copyText(gitlabUrl, t('ui.copied', 'Zkopírováno'))}
                      >
                        <Copy size={12} />
                      </button>
                      <a
                        href={gitlabUrl}
                        target='_blank'
                        rel='noreferrer'
                        className='text-gray-500 hover:text-blue-400'
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                    {cloneUrl && (
                      <div className='flex items-center gap-2 px-3 py-2'>
                        <code className='flex-1 min-w-0 text-[11px] text-gray-400 font-mono truncate'>
                          git clone {cloneUrl}
                        </code>
                        <Button
                          size='sm'
                          variant='ghost'
                          icon={<Copy size={13} />}
                          onClick={() =>
                            copyText(`git clone ${cloneUrl}`, t('codable.settingsCloneCopied'))}
                        />
                      </div>
                    )}
                    {canOrch && (
                      <div className='flex items-center justify-between gap-3 px-3 py-2.5'>
                        <div className='text-[11px] text-gray-500'>{t('codable.deployFromGitHint')}</div>
                        <Button
                          size='sm'
                          variant='secondary'
                          icon={<RefreshCw size={14} />}
                          loading={deploying}
                          onClick={deployFromGit}
                        >
                          {t('codable.deploy')}
                        </Button>
                      </div>
                    )}
                  </>
                )
                : (
                  <div className='px-3 py-3 text-sm text-gray-500'>
                    {t('codable.settingsNoGitlab', 'Projekt zatím nemá GitLab repo.')}
                  </div>
                )}
            </div>

            <div className='rounded-xl border border-gray-800 bg-gray-900/40 p-3 space-y-2'>
              <div className='flex items-center justify-between gap-2'>
                <div className='text-sm text-white'>{t('codable.knowledgeTitle')}</div>
                <Button
                  size='sm'
                  variant='secondary'
                  icon={<FileText size={13} />}
                  onClick={() => onOpenKnowledge('sources')}
                >
                  {t('codable.settingsOpenSources', 'Otevřít zdroje')}
                </Button>
              </div>
              <p className='text-[11px] text-gray-500'>
                {instructionLen > 0
                  ? t('codable.settingsInstructionsLen', 'Instrukce: {{n}} znaků', {
                    n: instructionLen,
                  })
                  : t('codable.settingsInstructionsEmpty', 'Instrukce jsou prázdné.')}
              </p>
              <p className='text-[11px] text-gray-500'>
                {treeQ.isLoading
                  ? t('codable.knowledgeTreeLoading')
                  : t('codable.settingsDocsCount', '{{n}} souborů v docs/ a README', {
                    n: docs.length,
                  })}
                {hasReadme ? ' · README' : ''}
                {hasMemory ? ' · MEMORY' : ''}
              </p>
              {docs.length === 0 && !treeQ.isLoading && (
                <p className='text-[11px] text-amber-300/80'>
                  {t(
                    'codable.settingsNoDocs',
                    'V repu zatím nejsou zdroje (README / docs/*.md). Agent z nich nemá co číst.',
                  )}
                </p>
              )}
              {docs.length > 0 && (
                <ul className='space-y-1 max-h-36 overflow-y-auto'>
                  {docs.slice(0, 12).map((d) => (
                    <li key={d.path} className='text-[11px] font-mono text-gray-400 truncate'>
                      {d.path}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Section>

        {/* Zálohy — dosud v Codable nešlo zálohu projektu ani vidět, ani zapnout. */}
        {projectId && (
          <Section title={t('backup.projectBackupTitle', 'Zálohy projektu')}>
            <ProjectBackupCard projectId={projectId} />
          </Section>
        )}

        <Section title={t('codable.settingsTabMore', 'Další')}>
          <div className='space-y-1.5'>
            {envName && (
              <Link
                to={`/modules/orchestrator?env=${encodeURIComponent(envName)}`}
                className='flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 hover:border-gray-700 hover:bg-gray-800/40'
              >
                <Layers size={15} className='text-gray-400 shrink-0' />
                <div className='min-w-0 flex-1'>
                  <div className='text-sm text-white'>{t('codable.moveManage')}</div>
                  <div className='text-[11px] text-gray-500'>{t('codable.moveManageHint')}</div>
                </div>
                <ExternalLink size={14} className='text-gray-600 shrink-0' />
              </Link>
            )}
            <LinkRow
              icon={BookOpen}
              label={t('codable.settingsKnowledge')}
              hint={t('codable.settingsKnowledgeHint')}
              onClick={() => onOpenKnowledge('instructions')}
            />
            <LinkRow
              icon={Activity}
              label={t('codable.settingsActivity')}
              hint={t('codable.settingsActivityHint')}
              onClick={onOpenActivity}
            />
            <LinkRow
              icon={History}
              label={t('codable.settingsHistory')}
              hint={t('codable.settingsHistoryHint')}
              onClick={onOpenHistory}
            />
            <LinkRow
              icon={ListChecks}
              label={t('codable.settingsLog')}
              hint={t('codable.settingsLogHint')}
              onClick={onOpenProjectLog}
            />
            <LinkRow
              icon={Cpu}
              label={t('codable.settingsMyLlm', 'My LLM models')}
              hint={t(
                'codable.settingsMyLlmHint',
                'Your own API key (BYOK) — applies to you in all projects',
              )}
              onClick={onOpenLlmModels}
            />
          </div>
        </Section>
      </div>
    </Modal>
  );
}
